import { orderBy } from 'lodash';

import { CHANNEL } from 'src/data/channel';
import { GENERATION_TOOL } from 'src/data/generationTool';
import { FILTER_ASSET_TYPE } from 'src/data/performance';
import { SHORT_FORM_CONTENT_TYPE } from 'src/data/shortForm';

import { determineMetricTypes, getPerformanceGoalCalculate, KNOWN_METRIC } from './metrics';
import { DATA_SECTION } from './sections';

export const getShortFormInfoFromChannelCopy = (channel, copy) => {
  switch (channel) {
    case CHANNEL.instagram:
      return {
        contentType: SHORT_FORM_CONTENT_TYPE.instagramPost,
        generationTool: GENERATION_TOOL.instagramCaption,
      };

    case CHANNEL.facebookPages:
      return {
        contentType: SHORT_FORM_CONTENT_TYPE.facebookPost,
        generationTool: GENERATION_TOOL.facebookPost,
      };

    case CHANNEL.linkedinSocial:
      return {
        contentType: SHORT_FORM_CONTENT_TYPE.linkedinPost,
        generationTool: GENERATION_TOOL.linkedinPost,
      };

    case CHANNEL.twitterSocial:
      return {
        contentType: SHORT_FORM_CONTENT_TYPE.twitterPost,
        generationTool: GENERATION_TOOL.tweet,
      };

    case CHANNEL.google:
      return {
        contentType: SHORT_FORM_CONTENT_TYPE.googleSearchAd,
        generationTool:
          copy.textType === FILTER_ASSET_TYPE.title
            ? GENERATION_TOOL.googleHeadlineProduct
            : GENERATION_TOOL.googleDescriptionProduct,
      };

    case CHANNEL.facebook:
      return {
        contentType: SHORT_FORM_CONTENT_TYPE.facebookAd,
        generationTool:
          copy.textType === FILTER_ASSET_TYPE.title
            ? GENERATION_TOOL.facebookHeadlineProduct
            : GENERATION_TOOL.facebookPrimaryTextProduct,
      };

    case CHANNEL.linkedin:
      return {
        contentType: SHORT_FORM_CONTENT_TYPE.linkedinAd,
        generationTool:
          copy.textType === FILTER_ASSET_TYPE.title
            ? GENERATION_TOOL.linkedinHeadlineSingleImage
            : GENERATION_TOOL.linkedinIntroSingleImage,
      };

    case CHANNEL.salesforce:
    case CHANNEL.marketo:
    case CHANNEL.hubspot:
    case CHANNEL.braze:
    case CHANNEL.iterable:
    case CHANNEL.eloqua:
      return {
        contentType: SHORT_FORM_CONTENT_TYPE.email,
        generationTool:
          copy.textType === FILTER_ASSET_TYPE.title
            ? GENERATION_TOOL.emailSubject
            : GENERATION_TOOL.emailBody,
      };

    default:
      return null;
  }
};

export const createPerformanceDataToChartItemMapper = ({ goal, metricType }) => {
  const metricTypes = determineMetricTypes({
    goal,
    metricType,
  });
  const calculateGoal = getPerformanceGoalCalculate(goal);

  return (entry) => {
    const allInstances = entry.copy_performance_instances;

    // We care only about instances that have a start date and at least one suitable metric
    const metricsInstances = allInstances.filter(
      (inst) => inst.start_date && hasMetric(inst.performance_metrics, metricTypes)
    );
    if (metricsInstances.length === 0) {
      return null;
    }

    const sortedInstances = orderBy(metricsInstances, 'start_date', 'asc');
    const metrics = sortedInstances.flatMap((inst) => inst.performance_metrics);

    const impressions = aggregateImpressions(sortedInstances) || 0;
    const calculateMetric = (types) => aggregateMetrics(metrics, types);

    return {
      id: entry.id,
      text: entry.copy_text,
      copyType: entry.copy_type,
      date: sortedInstances[0].start_date,
      section: entry.generated_copy_asset ? DATA_SECTION.anyword : DATA_SECTION.other,
      goal: calculateGoal ? calculateGoal({ goal, metricType, impressions, calculateMetric }) : 0,
      value: impressions,
    };
  };
};

const aggregateImpressions = (instances) => {
  return instances.reduce((acc, item) => acc + item.impressions, 0);
};

const aggregateMetrics = (metrics, metricTypes = null) => {
  // Filter metrics by provided types, or, if no metric types are provided, use all metrics
  const filtered =
    !metricTypes || metricTypes.length === 0
      ? metrics
      : metrics.filter((item) => metricTypes.includes(item.type));

  // Sum up the values of the filtered metrics
  const sum = filtered.reduce((acc, item) => {
    const value = parseFloat(item.value) || 0;
    return acc + value;
  }, 0);

  // If CTR is requested, return the average CTR
  if (metricTypes?.includes(KNOWN_METRIC.ctr)) {
    return sum / filtered.length;
  }

  return sum;
};

const hasMetric = (metrics, metricTypes) => {
  return metrics.some((item) => metricTypes.includes(item.type));
};
